import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash, MapPin, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { ExtraLocationMapEditor } from './ExtraLocationMapEditor';

/**
 * Producer UI to manage multiple named stall/RV locations on a show.
 * Each location has: name, type (stall/rv), optional detail map image, barns/rows.
 * Reservations made at these locations are isolated from the "Main" chart.
 */
export const ExtraLocationsEditor = ({ showId }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addType, setAddType] = useState('stall');
  const [newName, setNewName] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [bulkPrefix, setBulkPrefix] = useState('Barn');
  const [bulkCount, setBulkCount] = useState(5);
  const [bulkType, setBulkType] = useState('stall');
  const [bulkAdding, setBulkAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/shows/${showId}/spot-map`);
      setLocations(data.extra_locations || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [showId]);

  useEffect(() => { load(); }, [load]);

  const addLocation = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error('Enter a location name');
      return;
    }
    try {
      await api.post(`/shows/${showId}/extra-locations`, { name, type: addType });
      toast.success(`Added "${name}"`);
      setNewName('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add location');
    }
  };

  // Bulk-add: generate placeholder barns named "{prefix} A", "{prefix} B", ...
  // up to the requested count (max 26 = letters). Uses Promise.allSettled so a
  // partial failure doesn't lose successful adds.
  const bulkAdd = async () => {
    const prefix = (bulkPrefix || '').trim() || 'Barn';
    const count = Math.max(1, Math.min(26, parseInt(bulkCount, 10) || 0));
    if (!count) {
      toast.error('Enter a count between 1 and 26');
      return;
    }
    setBulkAdding(true);
    const t = toast.loading(`Adding ${count} ${prefix}s...`);
    const letters = Array.from({ length: count }, (_, i) => String.fromCharCode(65 + i));
    const calls = letters.map((letter) =>
      api.post(`/shows/${showId}/extra-locations`, {
        name: `${prefix} ${letter}`,
        type: bulkType,
      })
    );
    const results = await Promise.allSettled(calls);
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const fail = results.length - ok;
    if (fail > 0) {
      toast.error(`Added ${ok} of ${count} (${fail} failed). Check your connection and try again.`, { id: t });
    } else {
      toast.success(`Added ${ok} ${prefix}s (${letters[0]}–${letters[ok - 1]})`, { id: t });
    }
    setBulkAdding(false);
    await load();
  };

  const removeLocation = async (loc) => {
    if (!window.confirm(`Delete location "${loc.name}"? Any reservations made here will also be affected.`)) return;
    setBusyId(loc.id);
    try {
      await api.delete(`/shows/${showId}/extra-locations/${loc.id}`);
      toast.success(`Deleted "${loc.name}"`);
      await load();
    } catch {
      toast.error('Failed to delete location');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mt-6 bg-[#0F0F0F] border border-[#262626] rounded-lg p-5" data-testid="extra-locations-editor">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <MapPin size={18} className="text-[#0096FF]" />
          <h3 className="text-base font-bold text-white" style={{ fontFamily: 'Outfit' }}>
            Additional Locations
          </h3>
          <span className="text-[11px] text-[#9E9E9E] bg-[#262626] px-2 py-0.5 rounded-full">
            {locations.length} extra
          </span>
        </div>
      </div>

      <p className="text-xs text-[#9E9E9E] mb-4 leading-relaxed">
        Use this when your facility has <strong>multiple separate sites</strong> (e.g. a north stabling area + a south RV lot).
        The "Main" chart above is always present. Each additional location has its own chart, detail map,
        barns/rows and its own set of reservations — a spot in "Main Barn A-1" is a different physical spot
        from "North Grounds Barn A-1".
      </p>

      {/* Create new — single */}
      <div className="bg-[#161616] border border-[#262626] rounded-md p-3 mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={addType}
            onChange={(e) => setAddType(e.target.value)}
            className="px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-xs focus:border-[#0096FF] focus:outline-none"
            data-testid="extra-loc-type"
          >
            <option value="stall">Stall</option>
            <option value="rv">RV</option>
          </select>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Location name (e.g. North Grounds)"
            className="flex-1 min-w-[160px] px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-xs focus:border-[#0096FF] focus:outline-none"
            data-testid="extra-loc-name-input"
          />
          <button
            onClick={addLocation}
            className="flex items-center gap-1 px-3 py-1.5 bg-[#0096FF] text-white text-xs font-bold rounded-md hover:bg-[#0096FF]/90 transition-colors"
            data-testid="extra-loc-add-btn"
          >
            <Plus size={12} weight="bold" /> Add
          </button>
        </div>
      </div>

      {/* Bulk add — generate Barn A..Z placeholders in one go */}
      <div className="bg-[#161616] border border-[#262626] rounded-md p-3 mb-4" data-testid="extra-loc-bulk-add">
        <div className="text-[11px] text-[#9E9E9E] mb-2">
          <strong className="text-white">Bulk add</strong> — generate multiple lettered barns at once (e.g. <em>Barn A, Barn B, Barn C…</em>). You can upload a chart per barn afterward.
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={bulkType}
            onChange={(e) => setBulkType(e.target.value)}
            className="px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-xs focus:border-[#0096FF] focus:outline-none"
            data-testid="extra-loc-bulk-type"
          >
            <option value="stall">Stall</option>
            <option value="rv">RV</option>
          </select>
          <input
            value={bulkPrefix}
            onChange={(e) => setBulkPrefix(e.target.value)}
            placeholder="Prefix"
            className="w-24 px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-xs focus:border-[#0096FF] focus:outline-none"
            data-testid="extra-loc-bulk-prefix"
            title='e.g. "Barn" produces "Barn A", "Barn B", ...'
          />
          <span className="text-[11px] text-[#9E9E9E]">×</span>
          <input
            type="number"
            min={1}
            max={26}
            value={bulkCount}
            onChange={(e) => setBulkCount(e.target.value)}
            className="w-16 px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-xs focus:border-[#0096FF] focus:outline-none"
            data-testid="extra-loc-bulk-count"
            title="How many barns (max 26 = A–Z)"
          />
          <span className="text-[11px] text-[#9E9E9E] flex-1 min-w-[140px]">
            preview: <span className="text-white font-semibold">
              {(() => {
                const n = Math.max(1, Math.min(26, parseInt(bulkCount, 10) || 0));
                if (!n) return '–';
                const p = (bulkPrefix || 'Barn').trim() || 'Barn';
                const first = `${p} A`;
                const last = `${p} ${String.fromCharCode(64 + n)}`;
                return n === 1 ? first : `${first} … ${last}`;
              })()}
            </span>
          </span>
          <button
            onClick={bulkAdd}
            disabled={bulkAdding}
            className="flex items-center gap-1 px-3 py-1.5 bg-[#AF52DE] text-white text-xs font-bold rounded-md hover:bg-[#AF52DE]/90 transition-colors disabled:opacity-40"
            data-testid="extra-loc-bulk-add-btn"
          >
            <Plus size={12} weight="bold" /> {bulkAdding ? 'Adding…' : `Add ${Math.max(1, Math.min(26, parseInt(bulkCount, 10) || 0))}`}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-[#9E9E9E] text-xs">Loading…</p>
      ) : locations.length === 0 ? (
        <p className="text-[#9E9E9E] text-xs italic">No additional locations yet. Your "Main" chart covers this show.</p>
      ) : (
        <div className="space-y-3">
          {locations.map((loc) => (
            <LocationCard
              key={loc.id}
              showId={showId}
              loc={loc}
              busy={busyId === loc.id}
              allLocations={locations}
              onRemove={() => removeLocation(loc)}
              onChanged={load}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const LocationCard = ({ showId, loc, busy, allLocations, onRemove, onChanged }) => {
  const [barns, setBarns] = useState(loc.chart || []);
  const [saving, setSaving] = useState(false);
  const [copyFromId, setCopyFromId] = useState('');
  const [copyMarkers, setCopyMarkers] = useState(true);
  const [copying, setCopying] = useState(false);

  useEffect(() => { setBarns(loc.chart || []); }, [loc.id]);

  const addBarn = () => setBarns([...barns, { barn: `Barn ${barns.length + 1}`, rows: [{ label: 'A', spots: 10, start_number: 1 }] }]);
  const updateBarn = (i, patch) => setBarns(barns.map((b, idx) => idx === i ? { ...b, ...patch } : b));
  const deleteBarn = (i) => setBarns(barns.filter((_, idx) => idx !== i));
  const addRow = (bi) => {
    const b = barns[bi];
    const nextLabel = String.fromCharCode(65 + (b.rows?.length || 0));
    updateBarn(bi, { rows: [...(b.rows || []), { label: nextLabel, spots: 10, start_number: 1 }] });
  };
  const updateRow = (bi, ri, patch) => {
    const b = barns[bi];
    updateBarn(bi, { rows: b.rows.map((r, idx) => idx === ri ? { ...r, ...patch } : r) });
  };
  const deleteRow = (bi, ri) => {
    const b = barns[bi];
    updateBarn(bi, { rows: b.rows.filter((_, idx) => idx !== ri) });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/shows/${showId}/extra-locations/${loc.id}`, { chart: barns });
      toast.success(`Saved "${loc.name}"`);
      onChanged && onChanged();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const totalSpots = barns.reduce((s, b) => s + (b.rows || []).reduce((s2, r) => s2 + (Number(r.spots) || 0), 0), 0);

  // Copy chart (and optionally markers) from another extra-location into this one.
  // Same-type only — copying a stall chart into an RV location makes no sense.
  const sameTypeOptions = (allLocations || []).filter((o) => o.id !== loc.id && o.type === loc.type);

  const copyFromSource = async () => {
    if (!copyFromId) {
      toast.error('Pick a source barn first');
      return;
    }
    const source = (allLocations || []).find((o) => o.id === copyFromId);
    if (!source) return;
    const sourceChart = source.chart || [];
    if (!sourceChart.length) {
      toast.error(`"${source.name}" has no chart to copy`);
      return;
    }
    // Confirm if the destination already has chart data we'd overwrite.
    if (barns.length > 0) {
      const ok = window.confirm(
        `Replace the current chart on "${loc.name}" with the chart from "${source.name}"? This cannot be undone.`
      );
      if (!ok) return;
    }
    setCopying(true);
    try {
      const payload = { chart: sourceChart };
      if (copyMarkers) payload.markers = source.markers || [];
      await api.put(`/shows/${showId}/extra-locations/${loc.id}`, payload);
      toast.success(`Copied chart from "${source.name}" to "${loc.name}"`);
      setBarns(sourceChart);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Copy failed');
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="bg-[#161616] border border-[#262626] rounded-md p-3" data-testid={`extra-loc-${loc.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
        <div className="flex items-center gap-2">
          <span className="text-white font-bold text-sm">{loc.name}</span>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${loc.type === 'stall' ? 'bg-[#0096FF]/20 text-[#0096FF]' : 'bg-[#FF9500]/20 text-[#FF9500]'}`}>
            {loc.type === 'stall' ? 'STALL' : 'RV'}
          </span>
          <span className="text-[11px] text-[#9E9E9E]">{totalSpots} spots</span>
        </div>
        <button
          onClick={onRemove}
          disabled={busy}
          className="text-[#9E9E9E] hover:text-[#FF3B30] transition-colors disabled:opacity-40"
          title="Delete location"
          data-testid={`extra-loc-delete-${loc.id}`}
        >
          <Trash size={14} weight="bold" />
        </button>
      </div>

      {/* Interactive map + marker drawing */}
      <div className="mb-3">
        <ExtraLocationMapEditor showId={showId} location={loc} onChanged={onChanged} />
      </div>

      {/* Copy chart from another same-type barn */}
      {sameTypeOptions.length > 0 && (
        <div className="bg-[#0A0A0A] border border-[#262626] rounded p-2 mb-3" data-testid={`extra-loc-copy-${loc.id}`}>
          <div className="text-[11px] text-[#9E9E9E] mb-1.5">
            <strong className="text-white">Same layout as another barn?</strong> Copy a chart instead of rebuilding it row-by-row.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={copyFromId}
              onChange={(e) => setCopyFromId(e.target.value)}
              className="flex-1 min-w-[140px] px-2 py-1 bg-[#161616] border border-[#262626] rounded text-white text-[11px] focus:border-[#0096FF] focus:outline-none"
              data-testid={`extra-loc-copy-source-${loc.id}`}
            >
              <option value="">Copy chart from…</option>
              {sameTypeOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                  {(o.chart || []).length ? ` (${(o.chart || []).reduce((s, b) => s + (b.rows || []).reduce((s2, r) => s2 + (Number(r.spots) || 0), 0), 0)} spots)` : ' (empty)'}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-1 text-[11px] text-[#9E9E9E] cursor-pointer">
              <input
                type="checkbox"
                checked={copyMarkers}
                onChange={(e) => setCopyMarkers(e.target.checked)}
                className="accent-[#AF52DE]"
                data-testid={`extra-loc-copy-markers-${loc.id}`}
              />
              also copy map markers
            </label>
            <button
              onClick={copyFromSource}
              disabled={copying || !copyFromId}
              className="px-3 py-1 bg-[#AF52DE] text-white text-[11px] font-bold rounded hover:bg-[#AF52DE]/90 disabled:opacity-40 transition-colors"
              data-testid={`extra-loc-copy-btn-${loc.id}`}
            >
              {copying ? 'Copying…' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-2">
        <button
          onClick={addBarn}
          className="flex items-center gap-1 px-2 py-1 bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] text-[11px] font-semibold rounded hover:text-white transition-colors"
          data-testid={`extra-loc-add-barn-${loc.id}`}
        >
          <Plus size={11} weight="bold" /> Barn
        </button>
      </div>

      {barns.length === 0 ? (
        <p className="text-[11px] text-[#9E9E9E] italic">No barns yet — click "Barn" to add one.</p>
      ) : (
        <div className="space-y-2">
          {barns.map((b, bi) => (
            <div key={bi} className="bg-[#0A0A0A] border border-[#262626] rounded p-2">
              <div className="flex items-center gap-2 mb-1.5">
                <input
                  value={b.barn || ''}
                  onChange={(e) => updateBarn(bi, { barn: e.target.value })}
                  placeholder="Barn name"
                  className="flex-1 px-2 py-1 bg-[#161616] border border-[#262626] text-white text-xs font-semibold rounded focus:border-[#0096FF] focus:outline-none"
                />
                <button
                  onClick={() => addRow(bi)}
                  className="text-[11px] text-[#9E9E9E] hover:text-white font-semibold px-2 py-0.5 border border-[#262626] rounded"
                >
                  + row
                </button>
                <button
                  onClick={() => deleteBarn(bi)}
                  className="text-[#9E9E9E] hover:text-[#FF3B30] transition-colors"
                  title="Remove barn"
                >
                  <X size={12} weight="bold" />
                </button>
              </div>
              <div className="space-y-1">
                {(b.rows || []).map((r, ri) => (
                  <div key={ri} className="flex items-center gap-1.5 text-[11px]">
                    <input
                      value={r.label || ''}
                      onChange={(e) => updateRow(bi, ri, { label: e.target.value })}
                      placeholder="Row"
                      className="w-16 px-1.5 py-0.5 bg-[#161616] border border-[#262626] text-white rounded focus:border-[#0096FF] focus:outline-none"
                    />
                    <span className="text-[#9E9E9E]">#</span>
                    <input
                      type="number"
                      min={1}
                      value={r.start_number || 1}
                      onChange={(e) => updateRow(bi, ri, { start_number: Number(e.target.value) || 1 })}
                      className="w-16 px-1.5 py-0.5 bg-[#161616] border border-[#262626] text-white rounded focus:border-[#0096FF] focus:outline-none"
                      title="Starting number"
                    />
                    <span className="text-[#9E9E9E]">×</span>
                    <input
                      type="number"
                      min={1}
                      value={r.spots || 0}
                      onChange={(e) => updateRow(bi, ri, { spots: Number(e.target.value) || 0 })}
                      className="w-16 px-1.5 py-0.5 bg-[#161616] border border-[#262626] text-white rounded focus:border-[#0096FF] focus:outline-none"
                      title="Number of spots"
                    />
                    <span className="text-[#9E9E9E]">spots</span>
                    <button
                      onClick={() => deleteRow(bi, ri)}
                      className="text-[#9E9E9E] hover:text-[#FF3B30] transition-colors"
                    >
                      <X size={10} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={save}
        disabled={saving}
        className="mt-2 w-full py-1.5 bg-[#34C759] hover:bg-[#34C759]/90 disabled:opacity-40 text-white text-xs font-bold rounded transition-colors"
        data-testid={`extra-loc-save-${loc.id}`}
      >
        {saving ? 'Saving…' : `Save ${loc.name}`}
      </button>
    </div>
  );
};
